import { useCallback, useState, type FormEvent, type ReactElement } from 'react';
import { useRequireAuth } from '../../contexts/AuthContext';
import { useCanvasSession } from '../../contexts/CanvasSessionContext';

export interface SiteActionsBarProps {
  /** When false, the bar stays mounted but hidden (overflow tool gates visibility). */
  open: boolean;
  /** Close panel and deselect the tldraw site-actions tool. */
  onDismiss: () => void;
}

/** Edit + publish controls when a site session is complete (Stage 08). */
export function SiteActionsBar({ open, onDismiss }: SiteActionsBarProps): ReactElement | null {
  const { session, requestSiteEdit, publishSite } = useCanvasSession();
  const requireAuth = useRequireAuth('to edit your site');
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState<'edit' | 'publish' | null>(null);

  const visible =
    session.phase === 'complete' ||
    session.phase === 'editing' ||
    session.phase === 'publishing' ||
    (session.phase === 'error' && Boolean(session.siteId));

  const onEdit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!requireAuth()) return;
      const trimmed = instruction.trim();
      if (!trimmed) return;
      setBusy('edit');
      try {
        await requestSiteEdit({ instruction: trimmed });
        setInstruction('');
      } finally {
        setBusy(null);
      }
    },
    [instruction, requireAuth, requestSiteEdit],
  );

  const onPublish = useCallback(async () => {
    if (!requireAuth()) return;
    setBusy('publish');
    try {
      await publishSite();
    } finally {
      setBusy(null);
    }
  }, [requireAuth, publishSite]);

  if (!visible || !session.siteId || !open) {
    return null;
  }

  return (
    <section className="site-actions-bar" data-testid="site-actions-bar" aria-label="Site actions">
      <button
        type="button"
        className="site-actions-bar__dismiss"
        onClick={onDismiss}
        aria-label="Close site actions"
        data-testid="site-actions-dismiss"
      >
        ×
      </button>
      <form className="site-actions-bar__edit" onSubmit={(e) => void onEdit(e)}>
        <input
          type="text"
          className="site-actions-bar__input"
          placeholder="Describe an edit (e.g. change headline to Welcome)"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          disabled={busy !== null}
          aria-label="Edit instruction"
        />
        <button type="submit" className="site-actions-bar__btn" disabled={busy !== null}>
          {busy === 'edit' ? 'Applying…' : 'Apply edit'}
        </button>
      </form>
      <button
        type="button"
        className="site-actions-bar__btn site-actions-bar__btn--publish"
        onClick={() => void onPublish()}
        disabled={busy !== null}
      >
        {busy === 'publish' ? 'Publishing…' : 'Publish site'}
      </button>
      {session.publishedUrl ? (
        <a
          className="site-actions-bar__link"
          href={session.publishedUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          View published
        </a>
      ) : null}
    </section>
  );
}
